/* eslint-disable camelcase */
import React, { useState, useEffect } from 'react'
import { css } from 'aphrodite'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Chip,
  Grid,
  Typography,
  IconButton,
  Box,
  InputAdornment,
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import CloseIcon from '@material-ui/icons/Close'
import SearchIcon from '@material-ui/icons/Search'
import RefreshIcon from '@material-ui/icons/Refresh'
import ArrowForwardIcon from '@material-ui/icons/ArrowForward'

import { ApiHelper } from '../../../common/helpers/ApiHelper'
import { Helper } from '../../../common/helpers/Helper'
import { AppConfig } from '../../../AppConfig'
import { AppStyles, AppTheme } from '../../../styles/AppTheme'
import { AppHelper } from '../../../helpers/AppHelper'
import {
  WORKFLOW_RECIPE_CATEGORIES,
} from './WorkflowHelper'
import { WorkflowRecipeCard } from './WorkflowRecipeCard'
import { WorkflowRecipeSkeleton } from './WorkflowRecipeSkeleton'

export function WorkflowCreateDialog(props) {
  const {
    open,
    onClose,
    orgId,
    onSelect,
    showToast,
  } = props

  // Recipes state
  const [recipes, setRecipes] = useState([])
  const [categories, setCategories] = useState(WORKFLOW_RECIPE_CATEGORIES)
  const [recipesLoading, setRecipesLoading] = useState(false)
  const [recipesError, setRecipesError] = useState(null)
  const [selectedCategory, setSelectedCategory] = useState('ALL')
  const [searchQuery, setSearchQuery] = useState('')

  // Currently chosen recipe item: { type: 'recipe', recipe } | null
  const [selectedChoice, setSelectedChoice] = useState(null)

  // Fetch workflow recipes catalog from backend API
  const fetchRecipes = async () => {
    setRecipesLoading(true)
    setRecipesError(null)

    try {
      const response = await ApiHelper.callAwait({
        ms: AppConfig.CDP_MS.ENDPOINT,
        method: 'GET',
        paths: ['miscs', 'workflow-recipes'],
        queryParams: { orgId },
      })

      if (response && Array.isArray(response.recipes)) {
        setRecipes(response.recipes)
        if (Array.isArray(response.categories) && response.categories.length > 0) {
          setCategories(response.categories)
        }
      } else {
        setRecipes([])
      }
    } catch (err) {
      const errorMsg = Helper.getErrorMsg(err) || 'Could not load workflow recipes from server.'
      setRecipesError(errorMsg)
      if (showToast) {
        showToast(errorMsg, 'warning')
      }
    } finally {
      setRecipesLoading(false)
    }
  }

  // Load recipes on dialog open
  useEffect(() => {
    if (open) {
      setSelectedChoice(null)
      setSearchQuery('')
      setSelectedCategory('ALL')
      fetchRecipes()
    }
  }, [open, orgId])

  // Filter recipes by category and search text
  const filteredRecipes = recipes.filter((r) => {
    if (!r) return false
    const matchesCategory =
      selectedCategory === 'ALL' || r.category === selectedCategory

    const q = searchQuery.trim().toLowerCase()
    const matchesSearch =
      !q ||
      (r.title && r.title.toLowerCase().includes(q)) ||
      (r.description && r.description.toLowerCase().includes(q)) ||
      (Array.isArray(r.tagsToAdd) && r.tagsToAdd.some((t) => t.toLowerCase().includes(q))) ||
      (r.category && r.category.toLowerCase().includes(q))

    return matchesCategory && matchesSearch
  })

  // Count recipes per category
  const categoryCounts = categories.reduce((acc, cat) => {
    acc[cat.key] = recipes.filter((r) => r.category === cat.key).length
    return acc
  }, {})

  const handleSelectRecipe = (recipe) => {
    setSelectedChoice({ type: 'recipe', recipe })
  }

  const handleNext = () => {
    if (!selectedChoice) return
    if (onSelect) {
      onSelect(selectedChoice)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='lg'
      fullWidth
      style={{ zIndex: 13000 }}
      classes={{ paper: css(AppHelper.isDialogScrollbarEnabled() && AppStyles.dialogScrollbar) }}
      PaperProps={{
        style: {
          maxHeight: 'calc(100vh - 64px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: AppTheme.borderRadius,
        },
      }}
    >
      {/* Fixed Header */}
      <DialogTitle
        style={{
          flexShrink: 0,
          borderBottom: '1px solid #E5E7EB',
          padding: '18px 24px',
          backgroundColor: '#FFFFFF',
        }}
      >
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Box>
            <Typography variant='h6' style={{ fontWeight: 700, color: '#111827', fontSize: 20 }}>
              Pick a Recipe
            </Typography>
            <Typography variant='body2' style={{ color: '#6B7280', marginTop: 2 }}>
              Choose a workflow and customize
            </Typography>
          </Box>
          <IconButton onClick={onClose} size='small' style={{ color: '#6B7280' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Scrollable Content */}
      <DialogContent
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          padding: '24px',
          backgroundColor: '#F9FAFB',
        }}
      >
        {/* Search & Refresh Bar */}
        <Box display='flex' alignItems='center' mb={2} style={{ gap: 12 }}>
          <TextField
            placeholder='Search recipes by title, description, or action...'
            variant='outlined'
            size='small'
            fullWidth
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon style={{ color: '#9CA3AF', fontSize: 20 }} />
                </InputAdornment>
              ),
              style: { backgroundColor: '#FFFFFF', borderRadius: 8 },
            }}
          />
          <IconButton
            size='small'
            onClick={fetchRecipes}
            title='Refresh Recipes'
            style={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #E5E7EB',
              borderRadius: 8,
              padding: 8,
            }}
          >
            <RefreshIcon fontSize='small' style={{ color: '#4B5563' }} />
          </IconButton>
        </Box>

        {/* Category Pills */}
        <Box display='flex' flexWrap='wrap' mb={3} style={{ gap: 8 }}>
          <Chip
            label={`All (${recipes.length})`}
            clickable
            size='small'
            color={selectedCategory === 'ALL' ? 'primary' : 'default'}
            variant={selectedCategory === 'ALL' ? 'default' : 'outlined'}
            onClick={() => setSelectedCategory('ALL')}
            style={{
              fontWeight: 600,
              fontSize: 12,
              borderRadius: 6,
              backgroundColor: selectedCategory === 'ALL' ? '#2563EB' : '#FFFFFF',
            }}
          />
          {categories.map((cat) => {
            const count = categoryCounts[cat.key] || 0
            const isSelected = selectedCategory === cat.key
            return (
              <Chip
                key={cat.key}
                label={`${cat.label} (${count})`}
                clickable
                size='small'
                color={isSelected ? 'primary' : 'default'}
                variant={isSelected ? 'default' : 'outlined'}
                onClick={() => setSelectedCategory(cat.key)}
                style={{
                  fontWeight: 600,
                  fontSize: 12,
                  borderRadius: 6,
                  backgroundColor: isSelected ? '#2563EB' : '#FFFFFF',
                }}
              />
            )
          })}
        </Box>

        {/* Recipe Cards Grid / Skeletons / Empty State */}
        {recipesLoading ? (
          <WorkflowRecipeSkeleton count={4} />
        ) : recipesError && recipes.length === 0 ? (
          <Box p={3} textAlign='center'>
            <Alert
              severity='warning'
              action={
                <Button color='inherit' size='small' onClick={fetchRecipes}>
                  Retry
                </Button>
              }
            >
              {recipesError}
            </Alert>
          </Box>
        ) : filteredRecipes.length === 0 ? (
          <Box
            p={5}
            textAlign='center'
            style={{
              backgroundColor: '#FFFFFF',
              borderRadius: 8,
              border: '1px dashed #D1D5DB',
            }}
          >
            <Typography variant='subtitle1' style={{ color: '#4B5563', fontWeight: 600 }}>
              No workflow recipes match your search.
            </Typography>
            <Typography variant='body2' style={{ color: '#9CA3AF', marginTop: 4 }}>
              Try searching with different keywords or clear the category filter.
            </Typography>
            <Box mt={2}>
              <Button
                variant='outlined'
                size='small'
                onClick={() => {
                  setSearchQuery('')
                  setSelectedCategory('ALL')
                }}
              >
                Clear Filters
              </Button>
            </Box>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {filteredRecipes.map((recipe) => {
              const isSelected =
                selectedChoice &&
                selectedChoice.type === 'recipe' &&
                selectedChoice.recipe &&
                selectedChoice.recipe.key === recipe.key

              return (
                <Grid item xs={12} sm={4} md={4} key={recipe.key || recipe.title}>
                  <WorkflowRecipeCard
                    recipe={recipe}
                    isSelected={isSelected}
                    onSelect={handleSelectRecipe}
                  />
                </Grid>
              )
            })}
          </Grid>
        )}
      </DialogContent>

      {/* Fixed Footer Actions */}
      <DialogActions
        style={{
          flexShrink: 0,
          borderTop: '1px solid #E5E7EB',
          padding: '16px 24px',
          backgroundColor: '#FFFFFF',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Button
          onClick={onClose}
          style={{ textTransform: 'none', color: '#6B7280', fontWeight: 600 }}
        >
          Cancel
        </Button>

        <Box display='flex' alignItems='center' style={{ gap: 16 }}>
          <Typography
            variant='body2'
            style={{
              color: selectedChoice ? '#1F2937' : '#9CA3AF',
              fontWeight: 500,
              fontSize: 13,
            }}
          >
            {selectedChoice && selectedChoice.recipe ? (
              <span>
                Selected Recipe: <strong>{selectedChoice.recipe.title}</strong>
              </span>
            ) : (
              'Choose a workflow recipe to continue'
            )}
          </Typography>

          <Button
            variant='contained'
            color='primary'
            disabled={!selectedChoice}
            onClick={handleNext}
            endIcon={<ArrowForwardIcon />}
            style={{
              textTransform: 'none',
              fontWeight: 600,
              padding: '8px 22px',
              borderRadius: 6,
              backgroundColor: selectedChoice ? '#2563EB' : undefined,
            }}
          >
            Next
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}
